import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Select from 'tio-ui/components/select';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { LANGUAGE_OPTIONS, isSupportedLocale } from 'tio-employee/services/locale-preference';
import type LocalePreferenceService from 'tio-employee/services/locale-preference';
import type { SupportedLocale } from 'tio-employee/services/locale-preference';

/** The language recommendation's action: saving a choice is what retires the item. */
export default class BetaHomeLocaleRecommendation extends Component {
  @service declare intl: IntlService;
  @service declare localePreference: LocalePreferenceService;
  @service declare notifications: NotificationsService;

  @tracked chosen: SupportedLocale | null = null;

  choose = (key: string) => {
    this.chosen = isSupportedLocale(key) ? key : null;
  };

  confirm = dropTask(async () => {
    const locale = this.chosen;
    if (!locale) return;
    let remembered: boolean;
    try {
      remembered = await this.localePreference.save(locale);
    } catch (error) {
      console.error(error);
      this.notifications.add(this.intl.t('profile.language.save_error'), { appearance: 'error' });
      return;
    }
    if (remembered) this.localePreference.reloadUnlessShowing(locale);
  });

  <template>
    <div class="tio-cluster" data-test-locale-recommendation>
      <Select
        @items={{LANGUAGE_OPTIONS}}
        @selected={{this.chosen}}
        @onChange={{this.choose}}
        @placeholder={{t "recommendation_card.locale.select"}}
        @ariaLabel={{t "recommendation_card.locale.select"}}
        @disabled={{this.confirm.isRunning}}
        @size="sm"
        class="w-full max-w-xs"
        data-test-locale-recommendation-select
      />
      <Button
        @intent="primary"
        @size="sm"
        @isRunning={{this.confirm.isRunning}}
        @isDisabled={{not this.chosen}}
        {{on "click" this.confirm.perform}}
        data-test-locale-recommendation-confirm
      >
        {{t "recommendation_card.locale.confirm"}}
      </Button>
    </div>
  </template>
}
